import { useTranslation } from 'react-i18next';
import { useId, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  updatePartnerOfferPrice,
  type UpdatePartnerOfferPricePayload,
} from '../../api/endpoints/admin';
import type { ShipmentResponse } from '../../api/types';
import formStyles from '../../components/domain/FormModal.module.css';
import { Button } from '../../components/ui/Button';
import confirmStyles from '../../components/ui/ConfirmDialog.module.css';
import { DialogShell } from '../../components/ui/DialogShell';
import { SupportAction } from '../../components/ui/SupportAction';
import { toast } from '../../components/ui/Toast';
import { useDialogDismiss } from '../../components/ui/dialogDismiss';
import { curr, marginLabel } from '../../utils/price';
import {
  PARTNER_PRICE_REASON_MAX,
  partnerPriceAmountValid,
  partnerPriceErrorMessage,
  partnerPriceLocked,
  partnerPriceReasonValid,
} from './partnerOfferPrice';
import { DialogTitleRow } from '../../help/DialogTitleRow';

interface Props {
  shipment: ShipmentResponse;
  onClose: () => void;
}

/** Lists that show the partner price (Shipments, Orders, user profile); the detail is replaced, not refetched. */
function invalidateLists(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    queryKey: ['admin', 'shipments'],
    predicate: (q) => q.queryKey[2] !== 'detail',
  });
  void queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] });
  void queryClient.invalidateQueries({ queryKey: ['admin', 'users', 'overview-shipments'] });
}

/**
 * Set, change or clear the per-order partner offer price (#288, rhino-api R36, Contract dnm-work#30):
 * whole rupees and a required reason, confirmed with the amount before it is sent (UX 3). The global
 * carrier margin is only a hint here and never fills the field. On success the detail cache is replaced
 * with the server's response (P3).
 */
export function EditPartnerPriceDialog({ shipment, onClose }: Props) {
  const queryClient = useQueryClient();
  const titleId = useId();
  const current = shipment.partnerOfferPrice ?? null;
  const { t } = useTranslation();
  const [amount, setAmount] = useState(current != null ? String(current) : '');
  const [reason, setReason] = useState('');
  const [step, setStep] = useState<'form' | 'confirm' | 'confirm-clear'>('form');
  const sending = useRef(false);

  const save = useMutation<ShipmentResponse, Error, UpdatePartnerOfferPricePayload>({
    mutationFn: (body) => updatePartnerOfferPrice(shipment.id, body),
    onSuccess: (updated) => {
      queryClient.setQueryData(['admin', 'shipments', 'detail', shipment.id], updated);
      invalidateLists(queryClient);
      const price = updated.partnerOfferPrice ?? null;
      toast.success(
        price == null ? 'Partner price cleared.' : `Partner price set to ${curr(price)}.`,
      );
      onClose();
    },
    onError: (error) => {
      if (partnerPriceLocked(error)) {
        void queryClient.invalidateQueries({
          queryKey: ['admin', 'shipments', 'detail', shipment.id],
        });
        invalidateLists(queryClient);
      }
    },
    onSettled: () => {
      sending.current = false;
    },
  });

  const busy = save.isPending;
  const amountValid = partnerPriceAmountValid(amount);
  const reasonValid = partnerPriceReasonValid(reason);
  const locked = partnerPriceLocked(save.error);
  const dirty = amount !== String(current ?? '') || reason.trim() !== '';
  const dismiss = useDialogDismiss({ kind: 'form', dirty, busy, onClose });
  const clearing = step === 'confirm-clear';
  const hint =
    shipment.carrierMarginPct != null && shipment.carrierFacingOfferedPrice != null
      ? `Hint: budget ${marginLabel(shipment.carrierMarginPct, '−')} = ${curr(shipment.carrierFacingOfferedPrice)}`
      : null;

  const send = () => {
    if (sending.current || !reasonValid || (!clearing && !amountValid)) return;
    sending.current = true;
    save.mutate({
      partnerOfferPrice: clearing ? null : Number(amount),
      reason: reason.trim(),
    });
  };

  const review = (e: FormEvent) => {
    e.preventDefault();
    if (amountValid && reasonValid) setStep('confirm');
  };

  const back = () => {
    save.reset();
    setStep('form');
  };

  const primaryLabel = clearing ? 'Clear price' : `Set ${curr(Number(amount))}`;

  return (
    <DialogShell
      dismiss={dismiss}
      labelledBy={titleId}
      scrimClassName={confirmStyles.backdrop}
      panelClassName={confirmStyles.modal}
    >
      <div className={confirmStyles.header}>
        <DialogTitleRow context="order-prices" title={current == null ? 'Set partner price' : 'Edit partner price'}>
          <h3 id={titleId} className={confirmStyles.title}>
            {current == null ? 'Set partner price' : 'Edit partner price'}
          </h3>
        </DialogTitleRow>
        <button
          type="button"
          className={confirmStyles.closeBtn}
          onClick={dismiss.requestClose}
          disabled={busy}
          aria-label={t('shipments.editPartnerPriceDialog.closeDialog')}
        >
          &times;
        </button>
      </div>

      {step === 'form' ? (
        <form onSubmit={review} className={confirmStyles.body}>
          <div className={confirmStyles.description}>
            {t('shipments.editPartnerPriceDialog.description')}
          </div>
          <div>
            <label className={formStyles.label} htmlFor="partner-price-amount">
              {t('shipments.editPartnerPriceDialog.amountLabel')}
            </label>
            <input
              id="partner-price-amount"
              className={formStyles.input}
              type="number"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-autofocus
            />
            {amount !== '' && !amountValid && (
              <p className={formStyles.hint}>{t('shipments.editPartnerPriceDialog.amountInvalid')}</p>
            )}
            {hint && <p className={formStyles.hint}>{hint}</p>}
          </div>
          <div className={confirmStyles.reasonWrapper}>
            <label className={formStyles.label} htmlFor="partner-price-reason">
              {t('shipments.editPartnerPriceDialog.reasonLabel')}
            </label>
            <textarea
              id="partner-price-reason"
              className={confirmStyles.textarea}
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('shipments.editPartnerPriceDialog.reasonPlaceholder')}
            />
            {reason !== '' && !reasonValid && (
              <p className={formStyles.hint}>{t('shipments.editPartnerPriceDialog.reasonInvalid')}</p>
            )}
            <p className={formStyles.hint}>
              {reason.trim().length}/{PARTNER_PRICE_REASON_MAX}
            </p>
          </div>
          <div className={confirmStyles.actions}>
            {current != null && (
              <Button
                variant="ghost"
                className={confirmStyles.secondaryAction}
                disabled={!reasonValid}
                onClick={() => setStep('confirm-clear')}
              >
                {t('shipments.editPartnerPriceDialog.clearPrice')}
              </Button>
            )}
            <Button variant="secondary" onClick={dismiss.requestClose}>
              {t('common.actions.cancel')}
            </Button>
            <Button variant="primary" type="submit" disabled={!amountValid || !reasonValid}>
              {t('shipments.editPartnerPriceDialog.reviewPrice')}
            </Button>
          </div>
        </form>
      ) : (
        <div className={confirmStyles.body}>
          <div className={confirmStyles.description}>
            {clearing ? (
              'Clear the partner price? Partners will see no price.'
            ) : (
              <>
                {t('shipments.editPartnerPriceDialog.confirmSet')}
                <strong>{curr(Number(amount))}</strong>?
              </>
            )}
          </div>
          <div className={confirmStyles.description}>{t('shipments.editPartnerPriceDialog.confirmReason', { reason: reason.trim() })}</div>
          {save.error && (
            <div className={formStyles.error} role="alert">
              {partnerPriceErrorMessage(save.error)}
            </div>
          )}
          {save.error && !locked && <SupportAction />}
          <div className={confirmStyles.actions}>
            {locked ? (
              <Button variant="primary" onClick={onClose}>
                {t('common.actions.close')}
              </Button>
            ) : (
              <>
                <Button variant="secondary" onClick={back} disabled={busy}>
                  {t('shipments.editPartnerPriceDialog.back')}
                </Button>
                <Button variant="primary" onClick={send} busy={busy}>
                  {busy ? 'Saving…' : save.error ? 'Try again' : primaryLabel}
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </DialogShell>
  );
}
